import React, { useState, useEffect } from 'react';
import { X, Edit, Calendar, FileText, CheckCircle, Trash2 } from 'lucide-react';
import { fetchSolutions } from '../utils/supabaseHelpers';

const ProblemDetailModal = ({ problem, onClose, onEdit, onUpdate, onDelete }) => {
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState(problem.status);
  const [solutions, setSolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(true);

  useEffect(() => {
    const loadSolutions = async () => {
      if (problem.id) {
        try {
          setLoadingSolutions(true);
          const fetchedSolutions = await fetchSolutions(problem.id);
          setSolutions(fetchedSolutions);
        } catch (error) {
          console.error('Failed to load solutions:', error);
          setSolutions([]);
        } finally {
          setLoadingSolutions(false);
        }
      }
    };
    
    loadSolutions();
  }, [problem.id]);

  const handleStatusUpdate = () => {
    onUpdate({ ...problem, status: newStatus });
    setShowStatusUpdate(false);
  };

  const getDomainColor = (domain) => {
    const colors = {
      mechanical: 'badge-blue',
      software: 'badge-purple',
      electronics: 'badge-green',
      management: 'badge-yellow'
    };
    return colors[domain] || 'badge-blue';
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'badge-blue',
      under_discussion: 'badge-yellow',
      solution_decided: 'badge-purple',
      in_progress: 'badge-orange',
      completed: 'badge-green'
    };
    return colors[status] || 'badge-blue';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'badge-blue',
      medium: 'badge-yellow',
      high: 'badge-orange',
      critical: 'badge-red'
    };
    return colors[priority] || 'badge-blue';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-detail-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-2xl font-bold flex-1 pr-4" data-testid="modal-title">{problem.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`badge ${getDomainColor(problem.domain)}`}>
              {problem.domain.charAt(0).toUpperCase() + problem.domain.slice(1)}
            </span>
            <span className={`badge ${getStatusColor(problem.status)}`}>
              {formatStatus(problem.status)}
            </span>
            <span className={`badge ${getPriorityColor(problem.priority)}`}>
              {problem.priority.charAt(0).toUpperCase() + problem.priority.slice(1)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <FileText size={20} className="text-blue-400" />
              Description
            </h3>
            <p className="text-gray-300" data-testid="modal-description">{problem.description}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Calendar size={16} />
                <span className="text-sm">Date Reported</span>
              </div>
              <p className="font-medium" data-testid="modal-date">
                {problem.date_reported || problem.dateReported || problem.created_at ? 
                  new Date(problem.date_reported || problem.dateReported || problem.created_at).toLocaleDateString() : 
                  'N/A'
                }
              </p>
            </div>
            {(problem.due_date || problem.dueDate) && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">Due Date</span>
                </div>
                <p className="font-medium" data-testid="modal-due-date">
                  {new Date(problem.due_date || problem.dueDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Solutions</h3>
            {loadingSolutions ? (
              <p className="text-gray-400">Loading solutions...</p>
            ) : solutions.length > 0 ? (
              <div className="space-y-3">
                {solutions.map((solution, index) => (
                  <div
                    key={solution.id || index}
                    className={`glass-card p-4 ${solution.is_chosen ? 'border-2 border-green-500' : ''}`}
                    data-testid={`solution-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      {solution.is_chosen && (
                        <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                      )}
                      <p className="text-gray-300 flex-1">{solution.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No solutions added yet.</p>
            )}
          </div>

          {/* Notes */}
          {problem.notes && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Notes</h3>
              <div className="glass-card p-4">
                <p className="text-gray-300 whitespace-pre-wrap" data-testid="modal-notes">{problem.notes}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          {problem.linkedDocuments && problem.linkedDocuments.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Linked Documents</h3>
              <div className="glass-card p-4">
                <p className="text-gray-400 text-sm">{problem.linkedDocuments.length} document(s) linked</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onEdit}
            className="btn-primary flex items-center gap-2"
            data-testid="modal-edit-btn"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => setShowStatusUpdate(!showStatusUpdate)}
            className="btn-secondary"
            data-testid="modal-update-status-btn"
          >
            Update Status
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="btn-secondary text-red-400 hover:bg-red-500/10 flex items-center gap-2 ml-auto"
              data-testid="modal-delete-btn"
            >
              <Trash2 size={18} />
              Delete
            </button>
          )}
          {showStatusUpdate && (
            <div className="flex-1 flex gap-2">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="glass-input flex-1"
                data-testid="status-select"
              >
                <option value="new">New</option>
                <option value="under_discussion">Under Discussion</option>
                <option value="solution_decided">Solution Decided</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <button
                onClick={handleStatusUpdate}
                className="btn-primary"
                data-testid="status-update-confirm-btn"
              >
                Update
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemDetailModal;
